import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useId } from "react";
import { useController, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useCurrentUser } from "@/app/session/useSession";
import { useCreateProject } from "@/features/projects/api";
import { ClientField } from "@/features/projects/components/ClientField";
import { LeadField } from "@/features/projects/components/LeadField";
import { ProjectDateFields } from "@/features/projects/components/ProjectDateFields";
import { ServiceChoiceCards } from "@/features/projects/components/ServiceChoiceCards";
import { useClientOptions, useStaffOptions } from "@/features/projects/hooks/usePickerOptions";
import { DESCRIPTION_MAX_LENGTH, NO_CLIENT } from "@/features/projects/lib/pickerOptions";
import {
  NEW_PROJECT_SERVER_FIELDS,
  newProjectSchema,
  type NewProjectInput,
  type NewProjectOutput,
} from "@/features/projects/lib/projectForm";
import { applyServerErrors } from "@/shared/forms/serverErrors";
import { useT } from "@/shared/i18n/useT";
import type { MessageId } from "@/shared/i18n/messages";
import { Button } from "@/shared/ui/Button";
import { Dialog } from "@/shared/ui/Dialog";
import { Field, describedBy } from "@/shared/ui/Field";
import { FormError } from "@/shared/ui/FormError";
import { Input } from "@/shared/ui/Input";
import { Textarea } from "@/shared/ui/Textarea";

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewProjectDialog({ open, onOpenChange }: NewProjectDialogProps) {
  const t = useT();
  const formId = useId();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const createProject = useCreateProject();
  const clients = useClientOptions();
  const staff = useStaffOptions(open);
  const form = useForm<NewProjectInput, unknown, NewProjectOutput>({
    resolver: zodResolver(newProjectSchema),
    defaultValues: {
      name: "",
      service: "web",
      client_id: NO_CLIENT,
      lead_id: me.id,
      starts_on: null,
      due_on: null,
      description: "",
    },
  });
  const { register, control, setError, reset } = form;
  const { errors, isSubmitting } = form.formState;
  const client = useController({ control, name: "client_id" });
  const lead = useController({ control, name: "lead_id" });
  const startsOn = useController({ control, name: "starts_on" });
  const dueOn = useController({ control, name: "due_on" });
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const onSubmit = form.handleSubmit(async ({ lead_id, ...values }) => {
    try {
      // The server makes the key from the name (First Project -> FP); the plan starts empty.
      // Without the team list (no access), it also makes the creator the lead.
      const project = await createProject.mutateAsync({
        ...values,
        ...(staff.available ? { lead_id } : {}),
      });
      toast.success(t("newProject.created", { key: project.key }));
      onOpenChange(false);
      await navigate(`/projects/${project.key}`);
    } catch (error) {
      applyServerErrors(error, setError, NEW_PROJECT_SERVER_FIELDS);
    }
  });

  const message = (id: string | undefined) => (id ? t(id as MessageId) : undefined);
  const nameError = message(errors.name?.message);
  const descriptionError = message(errors.description?.message);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("newProject.title")}
      description={t("newProject.description")}
      size="lg"
      initialFocus={() => {
        form.setFocus("name");
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} disabled={isSubmitting}>
            {isSubmitting ? t("newProject.creating") : t("newProject.create")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-5"
        onSubmit={(event) => void onSubmit(event)}
        noValidate
      >
        <Field id="project-name" label={t("newProject.name")} error={nameError}>
          <Input
            dir="auto"
            placeholder={t("newProject.namePlaceholder")}
            {...describedBy("project-name", nameError)}
            {...register("name")}
          />
        </Field>

        <ServiceChoiceCards {...register("service")} />

        {clients.available || staff.available ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {clients.available ? (
              <ClientField
                value={client.field.value}
                onChange={client.field.onChange}
                options={clients.options}
                loading={clients.loading}
                error={message(errors.client_id?.message)}
              />
            ) : null}
            {staff.available ? (
              <LeadField
                value={lead.field.value}
                onChange={lead.field.onChange}
                options={staff.options}
                loading={staff.loading}
                error={message(errors.lead_id?.message)}
              />
            ) : null}
          </div>
        ) : null}

        <ProjectDateFields
          startsOn={startsOn.field}
          dueOn={dueOn.field}
          startError={message(errors.starts_on?.message)}
          dueError={message(errors.due_on?.message)}
        />

        <Field
          id="project-description"
          label={t("newProject.descriptionLabel")}
          error={descriptionError}
        >
          <Textarea
            minRows={3}
            maxLength={DESCRIPTION_MAX_LENGTH}
            placeholder={t("newProject.descriptionPlaceholder")}
            className="max-h-60"
            {...describedBy("project-description", descriptionError)}
            {...register("description")}
          />
        </Field>

        <FormError errors={errors} />
      </form>
    </Dialog>
  );
}
